import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { makeStyles } from 'tss-react/mui'
import { useDispatch } from 'react-redux'
import { Security } from '../../proto/generated/security'
import {
    countActions,
    mapScoreToLabel,
    mergeActionCounts,
    scoreFromActions
} from '../../util/technicals'
import { setTechnicalsPanelOpen } from '../../reducer/home.reducer'
import { useTechnicalsData } from '../../pages/technicals/engine/useTechnicalsData'
import TechnicalsSummaryCard from './TechnicalsSummaryCard'

type TechnicalsSectionProps = {
    security: Security | undefined
}

const OVERVIEW_TIMEFRAME = '1D'

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(1.5, 2, 2)
    },
    header: {
        fontSize: '15px',
        fontWeight: 600,
        marginBottom: theme.spacing(1.5)
    },
    gauge: {
        maxWidth: 220,
        margin: '0 auto'
    },
    moreButton: {
        marginTop: theme.spacing(1.5),
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        justifyContent: 'space-between'
    }
}))

const TechnicalsSection = ({ security }: TechnicalsSectionProps) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()

    const { result, loading } = useTechnicalsData({
        security,
        timeframeId: OVERVIEW_TIMEFRAME,
        enabled: !!security
    })

    const handleOpenTechnicals = useCallback(() => {
        dispatch(setTechnicalsPanelOpen(true))
    }, [dispatch])

    const summary = useMemo(() => {
        const oscillatorRows = result?.oscillators ?? []
        const movingAverageRows = result?.movingAverages ?? []
        if (oscillatorRows.length === 0 && movingAverageRows.length === 0) return null

        const oscillatorScore = scoreFromActions(oscillatorRows)
        const movingAverageScore = scoreFromActions(movingAverageRows)
        const score = (oscillatorScore + movingAverageScore) / 2

        return {
            score,
            label: mapScoreToLabel(score),
            counts: mergeActionCounts(countActions(oscillatorRows), countActions(movingAverageRows))
        }
    }, [result])

    // Keep the previous gauge on screen while the next security loads, like the chart and
    // range sections do, instead of collapsing the section.
    const lastSummaryRef = useRef<typeof summary>(null)
    useEffect(() => {
        if (summary) {
            lastSummaryRef.current = summary
        } else if (!loading) {
            lastSummaryRef.current = null
        }
    }, [summary, loading])

    const displaySummary = summary ?? (loading ? lastSummaryRef.current : null)

    if (!security) return null

    if (!displaySummary) {
        if (!loading) return null
        return (
            <Box className={classes.container}>
                <Typography className={classes.header}>Technicals</Typography>
                <Skeleton variant="rounded" height={160} />
            </Box>
        )
    }

    return (
        <Box
            className={classes.container}
            sx={{ opacity: loading ? 0.72 : 1, transition: 'opacity 0.2s ease' }}
        >
            <Typography className={classes.header}>Technicals</Typography>
            <Box className={classes.gauge}>
                <TechnicalsSummaryCard
                    title="Summary"
                    label={displaySummary.label}
                    score={displaySummary.score}
                    counts={displaySummary.counts}
                    emphasized
                    showTitle={false}
                    showCounts={false}
                    animated={false}
                />
            </Box>
            <Button
                fullWidth
                variant="outlined"
                color="inherit"
                size="small"
                className={classes.moreButton}
                endIcon={<ChevronRightIcon fontSize="small" />}
                onClick={handleOpenTechnicals}
            >
                More technicals
            </Button>
        </Box>
    )
}

export default memo(TechnicalsSection)
